import React, { useState, useEffect } from 'react';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonLoading, IonToast } from '@ionic/react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Stack,
  Alert
} from '@mui/material';
import { Visibility, CheckCircle, Pending, Warning, Inventory, Refresh } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getPendingBarRequestsWithItems } from '../../../api/stock-transfer/stock-transfer';

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  '&.pending': {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  '&.completed': {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  '&.partial': {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
}));

interface RequestItem {
  id: string;
  product_id: string;
  name: string;
  quantity: number;
  available: number;
}

interface TransferRequest {
  id: string;
  request_id: string;
  requested_by: string;
  requested_by_id: string;
  items: RequestItem[];
  status: 'pending' | 'completed' | 'partial';
  created_at: string;
  notes?: string;
}

const RequisitionPage: React.FC = () => {
  const [requests, setRequests] = useState<TransferRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<TransferRequest | null>(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await getPendingBarRequestsWithItems();
      setRequests(response.data.data);
    } catch (error) {
      console.error(error);
      setToastMessage('Failed to fetch transfer requests');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const openViewDialog = (request: TransferRequest) => {
    setCurrentRequest(request);
    setViewDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle fontSize="small" />;
      case 'partial': return <Warning fontSize="small" />;
      default: return <Pending fontSize="small" />;
    }
  };

  const getItemStatus = (item: RequestItem) => {
    if (item.available === 0) return 'Out of stock';
    if (item.available >= item.quantity) return 'Available';
    return 'Low stock';
  };

  const getItemStatusColor = (item: RequestItem) => {
    if (item.available === 0) return 'error';
    if (item.available >= item.quantity) return 'success';
    return 'warning';
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Warehouse Transfer Requests</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2">
                Transfer Requests
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<Refresh />}
                onClick={fetchRequests}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Requested By</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {loading ? <CircularProgress size={24} /> : 'No transfer requests found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests.map((request, i) => (
                      <TableRow key={i}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{request.requested_by}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${request.items.length} items`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(request.created_at)}</TableCell>
                        <TableCell>
                          <StatusChip 
                            label={request.status} 
                            className={request.status}
                            icon={getStatusIcon(request.status)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={() => openViewDialog(request)}
                                color="primary"
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>

        {/* View Details Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {currentRequest ? `Request Details (ID: ${currentRequest.request_id})` : 'Request Details'}
          </DialogTitle>
          <DialogContent dividers>
            {currentRequest && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1">
                    <strong>Requested by:</strong> {currentRequest.requested_by}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Date:</strong> {formatDate(currentRequest.created_at)}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Status:</strong> 
                    <StatusChip 
                      label={currentRequest.status} 
                      className={currentRequest.status}
                      icon={getStatusIcon(currentRequest.status)}
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  {currentRequest.notes && (
                    <Typography variant="subtitle1" sx={{ mt: 1 }}>
                      <strong>Notes:</strong> {currentRequest.notes}
                    </Typography>
                  )}
                </Box>

                {currentRequest.items.some(item => item.available < item.quantity) && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Some items have insufficient stock available
                  </Alert>
                )}
                
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Requested</TableCell>
                        <TableCell align="right">Available</TableCell>
                        <TableCell align="right">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentRequest.items.map(item => (
                        <TableRow key={item.product_id}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{item.available}</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={getItemStatus(item)}
                              size="small"
                              color={getItemStatusColor(item)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
        </Dialog>

        <IonLoading isOpen={loading} message={'Loading...'} />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
        />
      </IonContent>
    </IonPage>
  );
};

export default RequisitionPage;